import React, { useState, useRef, useEffect } from 'react';

const BASE_COLS = [
  { key: 'company',         label: 'Company',   editable: 'text',   w: '110px' },
  { key: 'role',            label: 'Role',      editable: 'text',   w: '145px' },
  { key: 'status',          label: 'Status',    editable: 'select', options: ['Saved','Applied','Screen','Interviewing','Offer','Withdrawn','Ghosted','Rejected','Accepted'], w: '112px' },
  { key: 'location',        label: 'Location',  editable: 'text',   w: '110px' },
  { key: 'salary',          label: 'Salary',    editable: 'text',   w: '105px' },
  { key: 'required_degree', label: 'Degree',    editable: 'text',   w: '90px'  },
  { key: 'keywords',        label: 'Skills',    editable: 'skills', w: '148px' },
  { key: 'remote_type',     label: 'Work Mode', editable: 'select', options: ['On-site','Hybrid','Remote'], w: '96px' },
  { key: 'job_type',        label: 'Job Type',  editable: 'select', options: ['Full-time','Internship','Part-time','Contract'], w: '96px' },
  { key: 'applied_date',    label: 'Applied',   editable: 'date',   w: '88px'  },
  { key: 'deadline',        label: 'Deadline',  editable: 'date',   w: '92px'  },
];

const DEFAULT_CHECKLISTS = {
  Saved:        ['Research the company', 'Tailor resume to job description', 'Write cover letter', 'Check referral network'],
  Applied:      ['Research company culture', 'Tailor resume', 'Write personalized cover letter', 'Connect with employee on LinkedIn'],
  Screen:       ['Review job description again', 'Prepare 2-min intro pitch', 'Research company news', 'Have questions ready'],
  Interviewing: ['Prepare STAR method stories', 'Research your interviewer', 'Prepare 5 questions to ask', 'Do a mock interview'],
  Offer:        ['Evaluate total comp package', 'Research market salary', 'Review benefits', 'Negotiate if needed'],
  Accepted:     ['Sign and return offer letter', 'Set start date', 'Resign from current role'],
  Rejected:     ['Send thank-you / feedback request', 'Keep connection on LinkedIn'],
};

const CUSTOM_COLS_KEY = 'applyai_custom_cols';
function loadCustomCols() { try { return JSON.parse(localStorage.getItem(CUSTOM_COLS_KEY) || '[]'); } catch { return []; } }
function saveCustomCols(cols) { localStorage.setItem(CUSTOM_COLS_KEY, JSON.stringify(cols)); }

function fmt(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
}

const HeartIcon = ({ filled }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill={filled ? '#f43f5e' : 'none'} stroke={filled ? '#f43f5e' : '#cbd5e1'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
);

function LocalSelect({ value, options, onChange, className }) {
  const [val, setVal] = React.useState(value);
  React.useEffect(() => { setVal(value); }, [value]);
  return (
    <select
      className={className(val)}
      value={val}
      onChange={e => { setVal(e.target.value); onChange(e.target.value); }}
    >
      {options.map(o => <option key={o}>{o}</option>)}
    </select>
  );
}

// ── Inline row expansion ──────────────────────────────────────────────
function RowExpand({ app, colCount, API_URL, onUpdate }) {
  const [notes, setNotes] = useState(app.notes || '');
  const [notesSaved, setNotesSaved] = useState(false);
  const [checklist, setChecklist] = useState(() => {
    const stored = Array.isArray(app.checklist) ? app.checklist : [];
    if (stored.length > 0) return stored;
    return (DEFAULT_CHECKLISTS[app.status] || []).map((text, i) => ({ id: String(i), text, completed: false }));
  });
  const [newItem, setNewItem] = useState('');
  const notesTimer = useRef(null);

  useEffect(() => { setNotes(app.notes || ''); }, [app.notes]);

  const handleNotesChange = (val) => {
    setNotes(val);
    clearTimeout(notesTimer.current);
    notesTimer.current = setTimeout(async () => {
      const res = await fetch(`${API_URL}/api/applications/${app.id}/notes`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes: val }),
      });
      if (res.ok) { onUpdate(await res.json()); setNotesSaved(true); setTimeout(() => setNotesSaved(false), 2000); }
    }, 1000);
  };

  const saveChecklist = async (updated) => {
    setChecklist(updated);
    const res = await fetch(`${API_URL}/api/applications/${app.id}/checklist`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ checklist: updated }),
    });
    if (res.ok) onUpdate(await res.json());
  };

  const toggleItem = (id) => saveChecklist(checklist.map(i => i.id === id ? { ...i, completed: !i.completed } : i));
  const removeItem = (id) => saveChecklist(checklist.filter(i => i.id !== id));
  const addItem = () => {
    if (!newItem.trim()) return;
    saveChecklist([...checklist, { id: Date.now().toString(), text: newItem.trim(), completed: false }]);
    setNewItem('');
  };

  const done = checklist.filter(i => i.completed).length;
  const keywords = Array.isArray(app.keywords) ? app.keywords : [];

  return (
    <tr className="row-expand-tr">
      <td colSpan={colCount} className="row-expand-td">
        <div className="row-expand-body">
          <div className="row-expand-grid">

            {/* Notes */}
            <div className="row-expand-col">
              <div className="row-expand-col-title">
                Notes
                {notesSaved && <span className="row-expand-saved">✓ Saved</span>}
              </div>
              <textarea
                className="row-expand-notes"
                value={notes}
                onChange={e => handleNotesChange(e.target.value)}
                placeholder="Interview prep, recruiter details, follow-up tasks…"
                rows={6}
              />
            </div>

            {/* Checklist */}
            <div className="row-expand-col">
              <div className="row-expand-col-title">
                Checklist
                <span className="row-expand-badge">{done}/{checklist.length}</span>
              </div>
              <div className="row-expand-checklist">
                {checklist.map(item => (
                  <div key={item.id} className="row-expand-check-item">
                    <input
                      type="checkbox"
                      id={`rxc-${app.id}-${item.id}`}
                      checked={item.completed}
                      onChange={() => toggleItem(item.id)}
                    />
                    <label htmlFor={`rxc-${app.id}-${item.id}`} className={item.completed ? 'done' : ''}>
                      {item.text}
                    </label>
                    <button className="row-expand-remove" onClick={() => removeItem(item.id)}>✕</button>
                  </div>
                ))}
                <div className="row-expand-add-row">
                  <input
                    className="row-expand-add-input"
                    value={newItem}
                    onChange={e => setNewItem(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addItem()}
                    placeholder="Add task…"
                  />
                  <button className="row-expand-add-btn" onClick={addItem}>+</button>
                </div>
              </div>
            </div>

            {/* Skills */}
            <div className="row-expand-col">
              <div className="row-expand-col-title">
                Skills
                {keywords.length > 0 && <span className="row-expand-badge">{keywords.length}</span>}
              </div>
              {keywords.length > 0
                ? <div className="row-expand-skills">{keywords.map(k => <span key={k} className="kw-pill">{k}</span>)}</div>
                : <p className="row-expand-empty">No skills extracted yet. Use Edit → extract from JD.</p>
              }
              {app.job_url && (
                <a href={app.job_url} target="_blank" rel="noopener noreferrer" className="row-expand-job-link">
                  View job posting ↗
                </a>
              )}
            </div>

          </div>
        </div>
      </td>
    </tr>
  );
}

export default function TableView({ applications, contacts = [], onEdit, onDelete, onArchive, onStatusChange, onRatingChange, onFieldChange, onRowClick, onContactClick, selectedId, API_URL, onUpdate }) {
  const [sortKey, setSortKey] = useState('applied_date');
  const [sortDir, setSortDir] = useState('desc');
  const [editCell, setEditCell] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [customCols, setCustomCols] = useState(loadCustomCols);
  const [addingCol, setAddingCol] = useState(false);
  const [newColName, setNewColName] = useState('');

  const sort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sorted = [...applications].sort((a, b) => {
    let av = a[sortKey] ?? '';
    let bv = b[sortKey] ?? '';
    if (sortKey.includes('date') || sortKey === 'deadline') {
      av = av ? new Date(av) : new Date(0);
      bv = bv ? new Date(bv) : new Date(0);
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const arrow = (key) => sortKey === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const startEdit = (e, appId, field, currentValue) => {
    e.stopPropagation();
    let value = currentValue ?? '';
    if (field.includes('date') || field === 'deadline') if (value) value = new Date(value).toISOString().split('T')[0];
    setEditCell({ id: appId, field, value: String(value) });
  };

  const commitEdit = (appId, field, originalValue) => {
    if (!editCell || editCell.id !== appId || editCell.field !== field) return;
    const newVal = editCell.value;
    setEditCell(null);
    if (newVal === String(originalValue ?? '')) return;
    onFieldChange(appId, { [field]: newVal });
  };

  const handleKey = (e, appId, field, originalValue) => {
    if (e.key === 'Enter') commitEdit(appId, field, originalValue);
    if (e.key === 'Escape') setEditCell(null);
  };

  const addCustomCol = () => {
    const name = newColName.trim();
    if (!name) return;
    const col = { id: `custom_${Date.now()}`, label: name };
    const updated = [...customCols, col];
    setCustomCols(updated); saveCustomCols(updated);
    setNewColName(''); setAddingCol(false);
  };

  const removeCustomCol = (id) => {
    const updated = customCols.filter(c => c.id !== id);
    setCustomCols(updated); saveCustomCols(updated);
  };

  const saveCustomField = (appId, colId, value) => {
    const app = applications.find(a => a.id === appId);
    const current = app?.custom_fields || {};
    onFieldChange(appId, { custom_fields: { ...current, [colId]: value } });
  };

  const getLinkedContacts = (appId) => contacts.filter(c => String(c.application_id) === String(appId));

  // total column count for colSpan
  const totalCols = BASE_COLS.length + 1 + 1 + customCols.length + 1; // +heart +contact +actions

  return (
    <div className="table-wrapper">
      <table className="app-table">
        <colgroup>
          {BASE_COLS.map(c => <col key={c.key} style={{ width: c.w }} />)}
          <col style={{ width: '42px' }}  />{/* Heart */}
          <col style={{ width: '80px' }}  />{/* Contact */}
          {customCols.map(c => <col key={c.id} style={{ width: '110px' }} />)}
          <col style={{ width: '100px' }} />{/* Actions */}
        </colgroup>
        <thead>
          <tr>
            {BASE_COLS.map(c => (
              <th key={c.key} onClick={() => sort(c.key)}>{c.label}{arrow(c.key)}</th>
            ))}
            <th title="Favourite" style={{ textAlign: 'center', cursor: 'default', overflow: 'visible', padding: '10px 4px' }}>
              <HeartIcon filled={false} />
            </th>
            <th>Contact</th>
            {customCols.map(c => (
              <th key={c.id} style={{ position: 'relative' }}>
                {c.label}
                <button className="col-remove-btn" onClick={e => { e.stopPropagation(); removeCustomCol(c.id); }} title={`Remove "${c.label}"`}>×</button>
              </th>
            ))}
            <th style={{ minWidth: '100px' }}>
              {addingCol ? (
                <div className="add-col-input-row" onClick={e => e.stopPropagation()}>
                  <input
                    autoFocus value={newColName}
                    onChange={e => setNewColName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') addCustomCol(); if (e.key === 'Escape') setAddingCol(false); }}
                    placeholder="Column name" className="add-col-input"
                  />
                  <button className="add-col-ok" onClick={addCustomCol}>✓</button>
                  <button className="add-col-cancel" onClick={() => setAddingCol(false)}>✕</button>
                </div>
              ) : (
                <button className="add-col-btn" onClick={() => setAddingCol(true)} title="Add custom column">+ Column</button>
              )}
            </th>
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td colSpan={totalCols} style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>No applications found</td></tr>
          )}
          {sorted.map(app => (
            <React.Fragment key={app.id}>
              <tr
                className={`${selectedId === app.id ? 'selected' : ''} ${expandedId === app.id ? 'row-expanded' : ''}`}
                onClick={() => !editCell && onRowClick(app)}
              >
                {BASE_COLS.map(col => {
                  const isEditing = editCell?.id === app.id && editCell?.field === col.key;

                  if (col.editable === 'skills') {
                    const kw = Array.isArray(app.keywords) ? app.keywords : [];
                    return (
                      <td key={col.key} style={{ maxWidth: col.w }}>
                        {kw.length === 0
                          ? <span className="cell-empty">—</span>
                          : <div className="kw-pills">
                              {kw.slice(0, 4).map(k => <span key={k} className="kw-pill">{k}</span>)}
                              {kw.length > 4 && <span className="kw-pill kw-more">+{kw.length - 4}</span>}
                            </div>
                        }
                      </td>
                    );
                  }

                  if (col.editable === 'select') {
                    const val = app[col.key] || col.options[0];
                    return (
                      <td key={col.key} onClick={e => e.stopPropagation()} style={{ padding: 0 }}>
                        <LocalSelect
                          value={val} options={col.options}
                          className={v => `table-native-select${col.key === 'status' ? ` tns-${v.toLowerCase()}` : ''}`}
                          onChange={v => onFieldChange(app.id, { [col.key]: v })}
                        />
                      </td>
                    );
                  }

                  if (isEditing) {
                    return (
                      <td key={col.key} className="editing-cell" onClick={e => e.stopPropagation()}>
                        <input
                          autoFocus
                          type={col.editable === 'date' ? 'date' : 'text'}
                          className="inline-input"
                          value={editCell.value}
                          onChange={e => setEditCell(prev => ({ ...prev, value: e.target.value }))}
                          onBlur={() => commitEdit(app.id, col.key, app[col.key])}
                          onKeyDown={e => handleKey(e, app.id, col.key, app[col.key])}
                        />
                      </td>
                    );
                  }

                  const raw = app[col.key];
                  let display = raw || <span className="cell-empty">—</span>;
                  if (col.editable === 'date' && raw) display = fmt(raw);
                  if (col.key === 'company') display = <strong>{raw}</strong>;
                  if (col.key === 'deadline' && raw) {
                    const isPast = new Date(raw) < new Date();
                    display = <span style={{ color: isPast ? '#ef4444' : '#f59e0b', fontWeight: 600 }}>{fmt(raw)}</span>;
                  }

                  return (
                    <td key={col.key} className="editable-cell" onClick={e => startEdit(e, app.id, col.key, raw)} title="Click to edit">
                      {display}
                    </td>
                  );
                })}

                {/* Heart */}
                <td
                  style={{ textAlign: 'center', cursor: 'pointer' }}
                  title={app.excitement_rating > 0 ? 'Remove from favourites' : 'Save to favourites'}
                  onClick={e => { e.stopPropagation(); onRatingChange(app.id, app.excitement_rating > 0 ? 0 : 5); }}
                >
                  <HeartIcon filled={app.excitement_rating > 0} />
                </td>

                {/* Contact */}
                {(() => {
                  const linked = getLinkedContacts(app.id);
                  return (
                    <td key="linked-contact" onClick={e => e.stopPropagation()} className="contact-cell">
                      {linked.length === 0 ? (
                        <span className="cell-empty">—</span>
                      ) : (
                        <div className="linked-contacts-stack">
                          {linked.slice(0, 2).map((c, idx) => (
                            <button
                              key={c.id}
                              className="contact-name-link"
                              title={`${c.name}${c.type ? ' · ' + c.type : ''}`}
                              onClick={e => { e.stopPropagation(); onContactClick && onContactClick(c); }}
                            >
                              {idx > 0 && <span style={{ color: '#cbd5e1', margin: '0 2px' }}>·</span>}
                              {c.name}
                            </button>
                          ))}
                          {linked.length > 2 && <span className="kw-pill kw-more" style={{ marginLeft: 3 }}>+{linked.length - 2}</span>}
                        </div>
                      )}
                    </td>
                  );
                })()}

                {/* Custom columns */}
                {customCols.map(col => {
                  const val = (app.custom_fields || {})[col.id] || '';
                  const isEditing = editCell?.id === app.id && editCell?.field === col.id;
                  if (isEditing) {
                    return (
                      <td key={col.id} className="editing-cell" onClick={e => e.stopPropagation()}>
                        <input
                          autoFocus className="inline-input" value={editCell.value}
                          onChange={e => setEditCell(prev => ({ ...prev, value: e.target.value }))}
                          onBlur={() => { const nv = editCell.value; setEditCell(null); if (nv !== val) saveCustomField(app.id, col.id, nv); }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') { const nv = editCell.value; setEditCell(null); if (nv !== val) saveCustomField(app.id, col.id, nv); }
                            if (e.key === 'Escape') setEditCell(null);
                          }}
                        />
                      </td>
                    );
                  }
                  return (
                    <td key={col.id} className="editable-cell" onClick={e => startEdit(e, app.id, col.id, val)} title="Click to edit">
                      {val || <span className="cell-empty">—</span>}
                    </td>
                  );
                })}

                {/* Actions */}
                <td onClick={e => e.stopPropagation()} style={{ padding: '6px 8px' }}>
                  <div style={{ display: 'flex', gap: '1px', alignItems: 'center' }}>
                    <button
                      className={`btn-icon view-btn ${expandedId === app.id ? 'view-btn-active' : ''}`}
                      onClick={() => setExpandedId(id => id === app.id ? null : app.id)}
                      title={expandedId === app.id ? 'Collapse' : 'Expand notes, checklist & skills'}
                    >
                      {expandedId === app.id ? '▲' : '▼'}
                    </button>
                    {app.job_url && <a href={app.job_url} target="_blank" rel="noopener noreferrer" className="btn-icon" title="Open job posting" style={{ textDecoration: 'none' }}>↗</a>}
                    <button className="btn-icon" onClick={() => onEdit(app)} title="Edit">✏️</button>
                    <button className="btn-icon" onClick={() => onDelete(app.id)} title="Delete">🗑️</button>
                  </div>
                </td>
              </tr>

              {/* Inline expansion */}
              {expandedId === app.id && (
                <RowExpand
                  app={app}
                  colCount={totalCols}
                  API_URL={API_URL}
                  onUpdate={onUpdate}
                />
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '10px' }}>
        {applications.length} application{applications.length !== 1 ? 's' : ''} · Click text cells to edit · Click column headers to sort · ▼ View to expand notes &amp; checklist
      </p>
    </div>
  );
}
